/**
 * AN OBLIGATION IS A LEARNER WHO IS OWED MONEY THE PLATFORM COULD NOT SEND.
 *
 * It exists because a refund FAILED AT STRIPE — insufficient balance, a dead charge, a refused
 * request — so the money never left and the row was opened so that the debt is not forgotten.
 * The money is the academy's: the platform takes no commission on the sale, holds none of the
 * funds and cannot pay this learner on the academy's behalf. Both branches below are the academy
 * recording what IT did or will not do.
 *
 * TWO OPERATIONS, ONE DIALOG, AND THEY ARE OPPOSITES:
 *
 *   Settle out of band   the academy has already paid the learner some other way. This is the
 *                        ONLY path to `payment_state: refunded` without a succeeded Stripe
 *                        refund, so it is the one an operator must not click to tidy a list.
 *   Write off            the academy is not going to pay the learner. There is a person on the
 *                        other end of that, and the copy does not soften it.
 *
 * NEITHER MOVES ANY MONEY. Both write a permanent row against the operator's name.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Badge, Banner, Button, Dialog, Field, TextAreaField } from '../../shared/ui';
import { formatMoney } from '../../shared/lib/pricing';
import { formatDate } from '../../shared/lib/catalogue';
import {
  OPS_COPY,
  reasonProblem,
  settleObligation,
  writeOffObligation,
  type WireObligation,
} from '../api/paymentOps';
import { ReasonField } from './ReasonField';
import { OpsFailureNotice, opsAlreadyHappened, useOperationId } from './CertificateChoices';

/**
 * THE SEAM UNDER-DECLARES BOTH ANSWERS, so the two extra fields are declared here.
 *
 * `api/paymentOps.ts` types both thunks as `{obligation_id, state, audit_action_id}`, but the
 * settle answer also carries `payment_state` and the write-off answer carries
 * `learner_was_paid: false` and DELIBERATELY NO `payment_state` — writing off does not change
 * what the order says was paid. Both are optional here because the fields are read, never
 * required: an absent one means the academy did not say, which is rendered as exactly that
 * rather than as `false`.
 */
interface WireSettleResponse {
  obligation_id: string;
  state: string;
  audit_action_id: string;
  payment_state?: string;
  learner_was_paid?: boolean;
}

interface WireWriteOffResponse {
  obligation_id: string;
  state: string;
  audit_action_id: string;
  learner_was_paid?: boolean;
}

/** Tagged, because the two branches have different answers and mean opposite things. */
export type ObligationOutcome =
  | { branch: 'settle'; response: WireSettleResponse }
  | { branch: 'write_off'; response: WireWriteOffResponse };

export const SETTLEMENT_NOTE_MIN_LENGTH = 10;

/** Non-blank, because the server refuses a blank one (`400 settlement_method_required`). */
export function settlementMethodProblem(method: string): string | null {
  if (!method.trim()) return 'Say how the money actually reached the learner.';
  return null;
}

/**
 * TEN CHARACTERS AND NO TWO-WORD RULE — a different field with a different rule.
 *
 * This is `note`, not `reason`, and the server checks it with `len(...) >= 10` and nothing else.
 * It is deliberately NOT `ReasonField`/`reasonProblem`: "bank transfer" satisfies this and would
 * satisfy the reason rule too, but "cheque 4471" satisfies this and fails the two-word reason
 * check, and telling an operator that a perfectly good settlement reference is not a reason is a
 * message about a rule that does not apply here. Merging the two would produce exactly that.
 */
export function settlementNoteProblem(note: string): string | null {
  const cleaned = note.trim();
  if (!cleaned) return 'A note is required.';
  if (cleaned.length < SETTLEMENT_NOTE_MIN_LENGTH) {
    return `At least ${SETTLEMENT_NOTE_MIN_LENGTH} characters — enough for somebody to match this against the academy’s own records later.`;
  }
  return null;
}

const CLOSED_STATES = new Set(['settled_out_of_band', 'written_off']);

export function ObligationDialog({
  obligation,
  onClose,
  onDone,
  onReload,
}: {
  obligation: WireObligation;
  onClose: () => void;
  /** Called when the operator dismisses the outcome. The parent owns the root query key. */
  onDone: (outcome: ObligationOutcome) => void;
  /**
   * A fresh read of the obligation list, offered when the SCREEN rather than the operator is
   * out of date (`already_done`). Optional; pass it wherever a refetch exists.
   */
  onReload?: () => void;
}) {
  const [branch, setBranch] = useState<'settle' | 'write_off' | null>(null);
  const [method, setMethod] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const operation = useOperationId();

  const settle = useMutation<ObligationOutcome, unknown, string>({
    mutationFn: async (operationId: string) => {
      if (branch === 'settle') {
        const response: WireSettleResponse = await settleObligation(obligation.id, {
          operationId,
          method,
          note,
        });
        return { branch: 'settle', response };
      }
      const response: WireWriteOffResponse = await writeOffObligation(obligation.id, {
        operationId,
        reason,
      });
      return { branch: 'write_off', response };
    },
  });

  const outcome = settle.data;
  const alreadyHappened = settle.isError && opsAlreadyHappened(settle.error);
  const alreadyClosed = CLOSED_STATES.has(obligation.state);

  const methodProblem = settlementMethodProblem(method);
  const noteProblem = settlementNoteProblem(note);
  const writeOffReasonProblem = reasonProblem(reason);
  const blocked =
    branch === null ||
    operation.id === null ||
    (branch === 'settle' ? methodProblem !== null || noteProblem !== null : writeOffReasonProblem !== null);

  const submit = () => {
    setAttempted(true);
    if (blocked || operation.id === null) return;
    // One id for this dialog, reused on every retry: a second press after a failure is the same
    // operation to the server, not a second settlement against the same debt.
    settle.mutate(operation.id);
  };

  const owed = formatMoney(obligation.amount_minor, obligation.currency);

  return (
    <Dialog
      title="What happened to the money this learner is owed?"
      wide
      onClose={onClose}
      footer={
        outcome ? (
          <Button variant="primary" onClick={() => onDone(outcome)}>
            Done
          </Button>
        ) : (
          <>
            {!alreadyHappened && !alreadyClosed && (
              <Button
                variant={branch === 'write_off' ? 'danger' : 'primary'}
                busy={settle.isPending}
                disabled={blocked}
                onClick={submit}
              >
                {branch === 'settle'
                  ? 'Record that the learner was paid'
                  : branch === 'write_off'
                    ? 'Record that the learner will not be paid'
                    : 'Record this'}
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      {operation.problem !== null && <Banner tone="danger">{operation.problem}</Banner>}

      <p>
        <strong>{owed}</strong> could not be sent to this learner.{' '}
        <Badge tone="warning">{obligation.state || 'state not stated'}</Badge>
      </p>
      {/* The server's sentence for WHY the refund failed at Stripe, verbatim: it names the
          Stripe condition, and a paraphrase would lose it. */}
      {obligation.failure_reason ? <p>{obligation.failure_reason}</p> : null}
      <p className="admin-muted">
        Opened {formatDate(obligation.opened_at) || obligation.opened_at || 'at an unrecorded time'}
        {' · '}
        {/* `''` MEANS UNASSIGNED. Printing it would render "Assigned to " followed by nothing,
            which reads as a name that failed to load. */}
        {obligation.assignee ? `assigned to ${obligation.assignee}` : 'nobody has picked this up'}
      </p>

      {outcome ? (
        outcome.branch === 'settle' ? (
          <>
            <Banner tone="success">
              Recorded: this academy has paid the learner {owed} outside Stripe. No money moved
              through this screen.
            </Banner>
            <p className="admin-muted">
              This obligation is now{' '}
              <strong>{outcome.response.state || 'not stated by the academy'}</strong>.
            </p>
            <p className="admin-muted">
              {outcome.response.payment_state ? (
                <>
                  The order&rsquo;s payment state is now{' '}
                  <strong>{outcome.response.payment_state}</strong> — reached without a succeeded
                  Stripe refund, on the strength of what you have just recorded.
                </>
              ) : (
                <>The academy did not say what the order&rsquo;s payment state became.</>
              )}
            </p>
          </>
        ) : (
          <>
            <Banner tone="warning">
              {outcome.response.learner_was_paid === false ? (
                <>
                  Recorded: <strong>this learner will not be paid</strong> the {owed} they are
                  owed. Nothing was sent to them, and nothing will be.
                </>
              ) : (
                <>
                  The write-off was recorded, but the academy did not confirm whether the learner
                  was paid. Check this order&rsquo;s audit trail before telling anybody it is
                  closed.
                </>
              )}
            </Banner>
            <p className="admin-muted">
              This obligation is now{' '}
              <strong>{outcome.response.state || 'not stated by the academy'}</strong>. The
              order&rsquo;s payment state is deliberately unchanged: writing off is not a refund
              and does not pretend to be one.
            </p>
          </>
        )
      ) : (
        <>
          {alreadyClosed && (
            <Banner tone="info">
              This obligation is already closed as <strong>{obligation.state}</strong>, so there is
              nothing left to record against it. Reload the screen if you were expecting it to be
              open.
            </Banner>
          )}

          {!alreadyClosed && (
            <>
              <fieldset className="ops-branch">
                <legend>Which of these happened?</legend>
                {/* NO DEFAULT. The two options are opposites — one says the learner has their
                    money, the other says they never will — and a pre-selected radio is how the
                    wrong one gets submitted by somebody moving quickly. */}
                <label className="ops-choice">
                  <input
                    type="radio"
                    name="obligation-branch"
                    value="settle"
                    checked={branch === 'settle'}
                    disabled={settle.isPending}
                    onChange={() => setBranch('settle')}
                  />
                  <span>
                    <strong>The academy has already paid the learner another way</strong>
                    <br />
                    <small className="admin-muted">
                      Bank transfer, cash, or a credit the learner has accepted.
                    </small>
                  </span>
                </label>
                <label className="ops-choice">
                  <input
                    type="radio"
                    name="obligation-branch"
                    value="write_off"
                    checked={branch === 'write_off'}
                    disabled={settle.isPending}
                    onChange={() => setBranch('write_off')}
                  />
                  <span>
                    <strong>The academy is not going to pay the learner</strong>
                    <br />
                    <small className="admin-muted">
                      The debt is written off and the learner keeps nothing back.
                    </small>
                  </span>
                </label>
              </fieldset>

              {branch === null && (
                <p className="admin-muted">
                  Choose one of the two above. They are opposites, so there is nothing sensible to
                  pre-select.
                </p>
              )}

              {branch === 'settle' && (
                <>
                  <Banner tone="warning">
                    <p>
                      <strong>
                        Only record this if the learner has actually been paid the {owed}.
                      </strong>{' '}
                      This is the only way this order becomes “refunded” without a succeeded Stripe
                      refund.
                    </p>
                    <p>
                      It moves no money itself. It records that the academy paid the learner
                      outside Stripe, and the order stops looking like a debt on that basis alone.
                    </p>
                  </Banner>
                  <Field
                    name="settlement-method"
                    label="How did the academy pay them?"
                    value={method}
                    disabled={settle.isPending}
                    onChange={(event) => setMethod(event.target.value)}
                    hint="Bank transfer, cash, credit — how the money actually reached them."
                    error={attempted && methodProblem ? methodProblem : undefined}
                  />
                  <TextAreaField
                    name="settlement-note"
                    label="What happened?"
                    rows={3}
                    value={note}
                    disabled={settle.isPending}
                    onChange={(event) => setNote(event.target.value)}
                    hint={`Recorded permanently against your name. At least ${SETTLEMENT_NOTE_MIN_LENGTH} characters — a reference somebody can match to the academy’s own records is ideal.`}
                    error={attempted && noteProblem ? noteProblem : undefined}
                  />
                </>
              )}

              {branch === 'write_off' && (
                <>
                  <Banner tone="danger">
                    <p>
                      <strong>
                        This records that this academy is not going to pay this learner the {owed}
                        they are owed.
                      </strong>{' '}
                      They are not refunded, they are not credited, and nobody contacts them about
                      it because of this.
                    </p>
                    <p>
                      It is not a refund and it does not change what the order says was paid. It is
                      a decision with a person on the other end of it, and it is permanent.
                    </p>
                  </Banner>
                  <ReasonField
                    value={reason}
                    onChange={setReason}
                    showProblem={attempted}
                    label="Why is the learner not being paid?"
                    disabled={settle.isPending}
                  />
                </>
              )}

              <p className="admin-muted">{OPS_COPY.auditIsPermanent}</p>
            </>
          )}
        </>
      )}

      {settle.isError && <OpsFailureNotice error={settle.error} onReload={onReload} />}
    </Dialog>
  );
}
